import User from '@/models/User';
import Role from '@/models/Role';
import Product from '@/models/Product';
import ProductCategory from '@/models/ProductCategory';
import ProductIngredient from '@/models/ProductIngredient';
import TaxRate from '@/models/TaxRate';
import Order from '@/models/Order';
import OrderItemComment from '@/models/OrderItemComment';
import Client from '@/models/Client';
import Sector from '@/models/Sector';
import Table from '@/models/Table';
import Grid from '@/models/Grid';
import Button from '@/models/Button';
import Shift from '@/models/Shift';
import CashRegister from '@/models/CashRegister';
import CashRegisterTransaction from '@/models/CashRegisterTransaction';
import Session from '@/models/Session';
import Shortcut from '@/models/Shortcut';
import UserTransaction from '@/models/UserTransaction';
import Pricelist from '@/models/Pricelist';
import Notification from '@/models/Notification';
import KitchenTicket from '@/models/KitchenTicket';
import Terminal from '@/models/Terminal';

import * as mutationTypes from '@/store/mutationTypes';
import ApiService from '@/services/ApiService';
import OrderService from '@/services/OrderService';
import CashRegisterService from '@/services/CashRegisterService';
import FiscalService from '@/services/FiscalService';
import TerminalService from '@/services/TerminalService';
import Store from '@/store/index';
import PrintService from '@/print/PrintService';
import Locale from '@/wrappers/Locale';
import AppStorage from '@/wrappers/AppStorage';
import Device from '@/wrappers/Device';
import i18n from '@/i18n';
import LocalNetworkService from '@/services/LocalNetworkService';
import ErrorReporter from '@/wrappers/ErrorReporter';

const TENANT_TOKEN_KEY = 'tn-token';
const TENANT_PKEY = 'tn-pkey';
const ORDERING_DEVICE_ID = 'ordering-device-id'

const TenantService = {
    /**
     * Register the Tenant and store the access token to TokenService.
     **/
    register(form) {
        return new Promise((resolve, reject) => {
            ApiService.post('/register', form, {
                timeout: 300000
            }).then(response => {
                this.setToken(response.data.access_token);
                this.setTenant(response.data.tenant);

                resolve(response);
            })
        });
    },

    /**
     * Login the Tenant and store the access token to TokenService.
     **/
    async login(form) {

        // If offline re-check connection and if still offline perform offline login
        if (!Store.getters.isOnline) {
            const isOnline = await Store.dispatch('connectionCheck');
            if(!isOnline) {
                return this.offlineLogin(form)
            }
        }

        try {
            const response = await ApiService.post('/login', form)

            this.setToken(response.data.access_token);
            this.setTenant(response.data.tenant);

            // store the tenant so that it can be used later on for offline login
            if(response.data.tenant) {
                Store.commit(mutationTypes.SET_LAST_TENANT, { lastTenant: response.data.tenant });
            }

            // toggle impersonate mode
            if(response.data.impersonate === true) {
                this.startImpersonate()
            } else {
                this.stopImpersonate()
            }

            // set cash register
            if(!CashRegisterService.getId() && response.data.cash_register) {
                CashRegisterService.setCashRegister(response.data.cash_register);
            }

            // sync data if needed
            if(response.data.tenant.venue) {
                await Store.dispatch('sync')
            }

            return response
        } catch(error) {
            this.removeToken();
            this.removeTenant();

            throw error
        }
    },

    /**
     * Perform offline login
     */
    async offlineLogin(form) {
        const lastLoggedTenant = Store.getters.lastTenant

        if(!lastLoggedTenant) {
            const errors = { email: [i18n.t('connection.internet_offline_message_not_allowed')] }
            form.errors = errors
            throw errors
        }

        let credentialsValid = lastLoggedTenant.email === form.email && bcrypt.compareSync(form.password, lastLoggedTenant.password)

        if(credentialsValid) {
            this.setTenant(lastLoggedTenant);
            return {
                data: { tenant: lastLoggedTenant }
            }
        } else {
            const errors = { email: [i18n.t('validation.credentials')] }
            form.errors = errors
            throw errors
        }
    },

    /**
     * Logout the current Tenant and remove the token from storage.
     *
     * Will also remove `Authorization Tenant <token>` header from future requests.
     */
    logout() {
        if (Store.getters.isOnline) {
            ApiService.post('/logout', {});
        }

        Store.commit(mutationTypes.SET_CUR_ORDER, { curOrder: null });
        Store.commit(mutationTypes.SET_CUR_ORDER_ITEM, { curOrderItem: null });

        this.removeToken();
        this.removeTenant();
    },

    loadData(initial = false) {
        if (!Store.getters.isOnline) {
            return Promise.resolve();
        }

        const params = {}
        if(!this.getPkey() || this.getPkey() === 'null') {
            params.include_key = true
        }

        if(initial) {
            params.initial = true
        }

        const ordersLastUpdatedAt = Order.getLastUpdatedAt();
        const productsLastUpdatedAt = Product.getLastUpdatedAt();

        params.orders_last_updated_at = ordersLastUpdatedAt ? ordersLastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null;
        params.products_last_updated_at = productsLastUpdatedAt ? productsLastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null;

        // Unsynced data
        let orders = Order.all().filter(order => !!order.sync_needed && order.items.length > 0)
        let userTransactions = UserTransaction.all().filter(transaction => !!transaction.sync_needed)
        let cashRegisterTransactions = CashRegisterTransaction.all().filter(transaction => !!transaction.sync_needed)
        let users = User.all().filter(user => !!user.sync_needed)
        let sessions = Session.all().filter(session => !!session.sync_needed)
        let kitchenTickets = KitchenTicket.all().filter(kt => !!kt.sync_needed)

        return new Promise((resolve, reject) => {
            ApiService.get('/load', { params: params })
                .then(async (response) => {

                    if(initial) {
                        // leave only unsynced data in store
                        Order.create({ data: orders.map(item => item.$toJson()), freeze: true })
                        UserTransaction.create({ data: userTransactions.map(item => item.$toJson()) })
                        CashRegisterTransaction.create({ data: cashRegisterTransactions.map(item => item.$toJson()) })
                        User.create({ data: users.map(item => item.$toJson()) })
                        Session.create({ data: sessions.map(item => item.$toJson()) })
                        KitchenTicket.create({ data: kitchenTickets.map(item => item.$toJson()) });

                        // reset products since on initial all products are returned from server
                        Product.create({ data: [], freeze: true });
                    }

                    // insert fresh data coming from server
                    Order.insert({ data: response.data.orders, freeze: true });
                    UserTransaction.insert({ data: response.data.user_transactions });
                    CashRegisterTransaction.insert({ data: response.data.cash_register_transactions });
                    User.insert({ data: response.data.users });
                    Session.insert({ data: response.data.sessions });
                    KitchenTicket.insert({ data: response.data.kitchen_tickets });

                    Product.insert({ data: response.data.products, freeze: true });

                    Role.create({ data: response.data.roles });
                    ProductCategory.create({ data: response.data.product_categories });
                    ProductIngredient.create({ data: response.data.product_ingredients });
                    TaxRate.create({ data: response.data.tax_rates });
                    Shift.create({ data: [response.data.shift] });
                    CashRegister.create({ data: response.data.cash_registers });
                    OrderItemComment.create({ data: response.data.order_item_comments });
                    Client.create({ data: response.data.clients });
                    Table.create({ data: response.data.tables });
                    Sector.create({ data: response.data.sectors });
                    Grid.create({ data: response.data.grids });
                    Button.create({ data: response.data.buttons });
                    Shortcut.create({ data: response.data.shortcuts });
                    Pricelist.create({ data: response.data.pricelists });
                    Terminal.create({ data: response.data.terminals });

                    Notification.insert({ data: response.data.unread_notifications });

                    if(response.data.pkey) {
                        this.setPkey(response.data.pkey);
                    }

                    Store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });
                    Store.commit(mutationTypes.SET_WORKING_DAYS, { workingDays: response.data.working_days });

                    OrderService.setLatestNumber(response.data.latest_order_number, true)
                    OrderService.setLatestInternalNumber(response.data.latest_order_internal_number)

                    if(response.data.latest_order_date) {
                        OrderService.setLatestOrderDate(response.data.latest_order_date)
                    }

                    let storedTerminal = await Terminal.current()
                    let storedTerminalId = await TerminalService.getId()
                    if(storedTerminalId && !storedTerminal) {
                        TerminalService.removeTerminal()
                    }

                    // TODO remove after the 7.0.56 release
                    const orderingDeviceId = await AppStorage.native().getItem(ORDERING_DEVICE_ID)
                    if(!storedTerminal && orderingDeviceId) {
                        AppStorage.native().deleteItem(ORDERING_DEVICE_ID)
                        const orderingDeviceTerminal = response.data.terminals.find(t => t.key === `orderingDevice${orderingDeviceId}`) || null
                        if(!orderingDeviceTerminal) {
                            ErrorReporter.reportMessage('Transition to terminals error: ordering device')
                            // CashRegisterService.removeCashRegister()
                            TerminalService.removeTerminal()
                        } else {
                            TerminalService.setTerminal(orderingDeviceTerminal)
                            storedTerminal = orderingDeviceTerminal
                        }
                    }

                    // TODO remove after the 7.0.56 release
                    const curCashRegister = CashRegister.current()
                    if(!storedTerminal && curCashRegister) {
                        const cashRegisterTerminal = response.data.terminals.find(t => t.key === `cashRegister${curCashRegister.id}`) || null
                        if(!cashRegisterTerminal) {
                            ErrorReporter.reportMessage('Transition to terminals error: cash register')
                            // CashRegisterService.removeCashRegister()
                            TerminalService.removeTerminal()
                        } else {
                            TerminalService.setTerminal(cashRegisterTerminal)
                        }
                    }

                    Store.commit(mutationTypes.SET_MODULES, { modules: response.data.modules || {} });
                    Store.commit(mutationTypes.SET_INTEGRATIONS, { integrations: response.data.integrations || {} });

                    resolve(response.data);
                }).catch(error => {
                    reject(error)
                })
        });
    },

    /**
     * Set Tenant
     */
    setTenant(tenant, startServices = true) {
        // Update Tenant in the Store
        Store.commit(mutationTypes.SET_TENANT, { tenant: tenant });

        if (!tenant) {
            ApiService.removeTenantHeader();
            ApiService.removeCashRegisterHeader();
            ApiService.unmountInterceptor401();
            ApiService.unmountInterceptor402();
        } else {
            ApiService.setTenantHeader();
            ApiService.mountInterceptor401();
            ApiService.mountInterceptor402();

            // Initialize services
            if (tenant.venue) {
                if (['en', 'me', 'sr', 'ba'].includes(tenant.venue.locale)) {
                    Locale.setLocale(tenant.venue.locale)
                    Locale.setLanguage(tenant.venue.language)
                    Locale.setCurrency(tenant.venue.currency)
                }

                if (CashRegisterService.getId()) {
                    ApiService.setCashRegisterHeader();
                }
            }
        }

        if(startServices) {
            this.startTenantServices()
        }
    },

    startTenantServices() {
        const tenant = Store.getters.tenant

        LocalNetworkService.reload()

        if (tenant?.venue) {
            FiscalService.init()
            PrintService.init()
        }
      },

    /**
     * Check if tenant contract has a one permission or
     * multiple specified permissions
     */
    contractAllows(permissions) {
        if (!Array.isArray(permissions)) {
            permissions = [permissions];
        }

        if(!permissions.length) {
            return true
        }

        const contracts = Store.getters.tenant.tenant_contracts;

        // TODO temporarily allow permissions to the tenants with no contracts
        // in the future this should be set to false
        if(!contracts || !contracts.length) {
            return true
        }

        const availablePermissions = contracts.some(c => ['Aria Standard', 'Aria Seasonal'].includes(c.contract.name))
            ? ['table_plan']
            : []

        let allowed = true;
        permissions.forEach(p => {
            if (!availablePermissions.includes(p)) {
                allowed = false;
            }
        });

        return allowed;
    },

    /**
     * Remove Tenant
     */
    removeTenant() {
        this.setTenant(null);
    },

    /**
     * Start impersonate mode
     */
    startImpersonate() {
        Store.commit(mutationTypes.SET_IMPERSONATE, { impersonate: true });
    },

    /**
     * Stop impersonate mode
     */
    stopImpersonate() {
        Store.commit(mutationTypes.SET_IMPERSONATE, { impersonate: false });
    },

    /**
     * Get Tenant Token
     */
    getToken() {
        return AppStorage.local().getItem(TENANT_TOKEN_KEY, null);
    },

    /**
     * Set Tenant Token
     */
    setToken(accessToken) {
        AppStorage.local().setItem(TENANT_TOKEN_KEY, accessToken, null);
    },

    /**
     * Remove Tenant Token
     */
    removeToken() {
        AppStorage.local().deleteItem(TENANT_TOKEN_KEY, null);
    },

    /**
     * Get Tenant Private Key
     */
    getPkey() {
        return AppStorage.local().getItem(TENANT_PKEY);
    },

    /**
     * Set Tenant Private Key
     */
    setPkey(pkey) {
        AppStorage.local().setItem(TENANT_PKEY, pkey);
    },
};

export default TenantService;

export { TenantService };
